import {
	type SustainabilityFormDto,
	SustainabilityFormStatus,
	type SustainabilityQuestionDto,
	type SustainabilitySectionDto
} from '../../models/sustainability';
import { selectProfileSectionsByProfileType } from './profile-sections.slice';

export type SustainabilityFormsState = {
	sustainabilityForms: {
		profileForms: Record<number, Record<number, SustainabilityFormDto>>;
	};
};

type StateWithFormsAndProfileSections = SustainabilityFormsState & {
	profileSections?: { sectionsByProfileType?: Record<number, SustainabilitySectionDto[]> };
};

type StateWithFormsAndQuestions = SustainabilityFormsState & {
	sustainabilityQuestions: {
		data: Record<number, Record<string, SustainabilityQuestionDto[]>>;
	};
};

export const selectAnswersByFormId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
): Record<number, string> => {
	const form = state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
	const answers = form?.answers ?? [];
	return answers.reduce<Record<number, string>>((acc, a) => {
		acc[a.sustainabilityQuestionId] = a.answer;
		return acc;
	}, {});
};

export const selectAnswerByQuestionId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number,
	questionId: number
): string | undefined => {
	return state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.answers?.find(
		(a) => a.sustainabilityQuestionId === questionId
	)?.answer;
};

export const selectFormByFormId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
): SustainabilityFormDto | undefined => {
	return state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
};

export const selectAnswerSections = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
) => state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.answerSections ?? [];

export const selectLatestInProgressFormByProfileId = (
	state: SustainabilityFormsState,
	profileId: number
): SustainabilityFormDto | undefined => {
	const byFormId = state.sustainabilityForms?.profileForms?.[profileId];
	if (!byFormId) return undefined;
	return Object.values(byFormId).find(
		(form) =>
			form.status === SustainabilityFormStatus.InProgress ||
			form.status === SustainabilityFormStatus.WaitingForApproval
	);
};

export const selectAnswersBySection = (
	state: StateWithFormsAndQuestions,
	profileId: number,
	formId: number,
	profileType: number,
	section: string
): Record<number, string> => {
	const form = state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
	const formAnswers = form?.answers ?? [];
	const questionsInSection = state.sustainabilityQuestions?.data?.[profileType]?.[section] ?? [];
	const questionIdsInSection = new Set(questionsInSection.map((q) => q.id));
	return formAnswers
		.filter((a) => questionIdsInSection.has(a.sustainabilityQuestionId))
		.reduce<Record<number, string>>((acc, a) => {
			acc[a.sustainabilityQuestionId] = a.answer;
			return acc;
		}, {});
};

export type SectionStatus = 'NotStarted' | 'InProgress' | 'Completed';

export const selectAppliciableSection = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number,
	section: string
): boolean => {
	const answerSections = selectAnswerSections(state, profileId, formId);
	const answerSection = answerSections.find((s) => s.section === section);

	if (answerSection?.isApplicable === null || answerSection?.isApplicable === undefined)
		return true;

	return answerSection?.isApplicable ?? true;
};

export const selectSectionStatus = (
	state: StateWithFormsAndQuestions,
	profileId: number,
	formId: number,
	profileType: number,
	section: string
): SectionStatus => {
	const questionsInSection = state.sustainabilityQuestions?.data?.[profileType]?.[section] ?? [];
	const answered = selectAnswersBySection(state, profileId, formId, profileType, section);
	const answeredCount = Object.keys(answered).length;
	const questionCount = questionsInSection.length;

	if (answeredCount === 0) return 'NotStarted';
	if (answeredCount > 0 && answeredCount < questionCount) return 'InProgress';
	return 'Completed';
};

export const selectCanSubmitFormForApproval = (
	state: StateWithFormsAndProfileSections,
	profileId: number,
	formId: number,
	profileType: number
): boolean => {
	const formSections = selectProfileSectionsByProfileType(state, profileType); //has the "hasApplicableCheckbox", meaning it can be disabled
	const form = selectFormByFormId(state, profileId, formId); // On the answer sections, we have the "isApplicable" property which indicates whether the section is applicable or not. If a section is not applicable, it should not prevent the form from being submitted for approval. Therefore, we need to filter out the non-applicable sections when determining if the form can be submitted for approval.

	const answerSectionsToCheck = formSections?.filter((answerSection) => {
		if (answerSection.hasApplicableCheckbox) {
			// check if the user has disabled the section, if so we should not add it to the sections we check for completeness
			return (
				form?.answerSections?.find((section) => section.section === answerSection.name)
					?.isApplicable ?? true
			);
		}
		// if it doesn't have the applicable checkbox, we should check it as normal, as there is no way for the user to disable it, so it should prevent form submission if not completed
		return true;
	});

	// only need the enabled sections, so filter out the disabled ones
	const enabledFormSections = form?.answerSections?.filter((s) => s.isApplicable !== false) ?? [];

	const completedCount = enabledFormSections.filter((s) => s.status === 'Completed').length;

	return (
		form?.status === SustainabilityFormStatus.InProgress &&
		enabledFormSections.length > 0 &&
		enabledFormSections.length === answerSectionsToCheck.length &&
		completedCount === enabledFormSections.length
	);
};

export const selectIsFormInProgress = (
	state: StateWithFormsAndProfileSections,
	profileId: number,
	formId: number
): boolean => {
	return (
		state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.status ===
		SustainabilityFormStatus.InProgress
	);
};
